import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../auth/context";
import { useReplicache } from "../replicache/context";

// /archive — read-only browse of the user's archived items, with a
// Restore action.  Archived rows live outside Replicache (excluded from
// pull + hash) so this page fetches them via a dedicated endpoint.
// After restoring, the next Replicache pull surfaces the row back into
// the live tree.
//
// Items are grouped by `archiveContext.archivedRootId` so a subtree
// archived as a single action displays together.  The ancestor trail
// captured at archive time is shown as ghost-text breadcrumbs — those
// rows may no longer exist in the live data, but we still display them
// so the user knows where the item came from.

type ArchivedItem = {
  id: string;
  groupId: string;
  parentId: string | null;
  type: "task" | "header";
  title: string;
  notes: string;
  status: string;
  isComplete: boolean;
  createdAt: string;
  updatedAt: string;
  archivedAt: string;
  archiveContext: {
    archivedRootId: string;
    groupName: string | null;
    ancestorTrail: Array<{ id: string; title: string }>;
  } | null;
};

type ArchiveGroup = {
  rootId: string;
  archivedAt: string;
  groupName: string | null;
  ancestorTrail: Array<{ id: string; title: string }>;
  // Items in this archive event, with the root first.
  items: ArchivedItem[];
};

export function Archive() {
  const { state } = useAuth();
  const rep = useReplicache();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [items, setItems] = useState<ArchivedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch("/api/archive", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error(`archive ${r.status}`);
      setItems((await r.json()) as ArchivedItem[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  // Group rows by archive event.  An archived subtree shares one
  // archivedRootId so a Restore button at the root can lift the whole
  // tree back at once.  Items without a context (defensive — every
  // archived row should have one) bucket under their own id.
  const groups = useMemo<ArchiveGroup[]>(() => {
    const byRoot = new Map<string, ArchiveGroup>();
    for (const it of items) {
      const rootId = it.archiveContext?.archivedRootId ?? it.id;
      let bucket = byRoot.get(rootId);
      if (!bucket) {
        bucket = {
          rootId,
          archivedAt: it.archivedAt,
          groupName: it.archiveContext?.groupName ?? null,
          ancestorTrail: it.archiveContext?.ancestorTrail ?? [],
          items: [],
        };
        byRoot.set(rootId, bucket);
      }
      bucket.items.push(it);
      // Keep the most recent archivedAt as the bucket timestamp.
      if (it.archivedAt > bucket.archivedAt) bucket.archivedAt = it.archivedAt;
    }
    // Sort items so the root is first, then by sortKey within the bucket.
    for (const g of byRoot.values()) {
      g.items.sort((a, b) => {
        if (a.id === g.rootId) return -1;
        if (b.id === g.rootId) return 1;
        return a.title.localeCompare(b.title);
      });
    }
    return [...byRoot.values()].sort((a, b) =>
      b.archivedAt.localeCompare(a.archivedAt),
    );
  }, [items]);

  const onRestore = async (rootId: string) => {
    if (!rep) return;
    setRestoringId(rootId);
    setNotice(null);
    setError(null);
    try {
      await rep.mutate.unarchiveItem({ id: rootId });
      // Optimistically drop the restored subtree from the page so the row
      // disappears immediately.  The next /api/archive fetch (on next
      // visit) confirms.
      setItems((prev) =>
        prev.filter((it) => it.archiveContext?.archivedRootId !== rootId),
      );
      setNotice(
        "Restored. If the original parent or group was deleted, the item was restored at the top of its group.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRestoringId(null);
    }
  };

  if (state.status === "loading") {
    return (
      <main className="mx-auto flex min-h-full max-w-2xl items-center justify-center p-8">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      </main>
    );
  }
  if (state.status === "unauthenticated") {
    return <Navigate to="/signin" replace />;
  }

  return (
    <main className="mx-auto max-w-2xl px-6 py-8">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Archive</h1>
        <Link
          to="/"
          className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Back
        </Link>
      </div>
      <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
        Archived items are excluded from live sync. Restore brings the
        subtree back; if the original parent or group was deleted, the
        item lands at the top of its group.
      </p>

      {error && (
        <p className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">
          {error}
        </p>
      )}
      {notice && (
        <p className="mb-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
          {notice}
        </p>
      )}

      {loading && items.length === 0 && (
        <p className="text-sm italic text-slate-400 dark:text-slate-500">Loading…</p>
      )}
      {!loading && groups.length === 0 && (
        <p className="text-sm italic text-slate-400 dark:text-slate-500">
          Nothing archived yet. Use the Archive button in a task's detail
          pane to hide it without deleting.
        </p>
      )}

      <ul className="space-y-3">
        {groups.map((g) => {
          const root = g.items.find((it) => it.id === g.rootId) ?? g.items[0]!;
          const rest = g.items.filter((it) => it.id !== root.id);
          return (
            <li
              key={g.rootId}
              className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div className="mb-1 text-[11px] text-slate-400 dark:text-slate-500">
                {/* Ghost breadcrumb: group name + ancestor chain captured at
                    archive time.  Rendered in muted italic so it reads as
                    historical context rather than a live link. */}
                {g.groupName ? (
                  <span className="italic">{g.groupName}</span>
                ) : (
                  <span className="italic">(group deleted)</span>
                )}
                {g.ancestorTrail.length > 0 && (
                  <>
                    <span className="px-1">·</span>
                    <span className="italic">
                      {g.ancestorTrail.map((a) => a.title || "(untitled)").join(" › ")}
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {root.title || "(untitled)"}
                  </div>
                  {root.notes && (
                    <div className="mt-0.5 line-clamp-2 text-xs text-slate-600 dark:text-slate-300">
                      {root.notes}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void onRestore(g.rootId)}
                  disabled={restoringId === g.rootId}
                  className="shrink-0 rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {restoringId === g.rootId ? "Restoring…" : "Restore"}
                </button>
              </div>
              <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                Archived {formatWhen(g.archivedAt)}
                {rest.length > 0 && ` · ${rest.length} child item${rest.length === 1 ? "" : "s"}`}
              </div>
              {rest.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-l border-slate-200 pl-3 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  {rest.map((c) => (
                    <li key={c.id} className="truncate">
                      {c.title || "(untitled)"}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </main>
  );
}

function formatWhen(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
